import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip,
} from 'chart.js';
import moment from 'moment';

// Added because the horizontal line does not extend all the way to the end using the normal way.
const extendPeerAverageLinePlugin = {
  id: 'extendPeerAverageLine',
  afterDatasetDraw: (chart) => {
    const datasets = chart.data.datasets;
    
    // Find the peer average dataset (which has label 'Peer Average')
    const peerAverageDataset = datasets.find(d => d.label === 'Peer Average');
    
    if (peerAverageDataset && peerAverageDataset.data.length > 0) {
      const peerAverage = peerAverageDataset.data[0]; // All values should be the same
      
      const { ctx, chartArea, scales } = chart;
      const { left, right } = chartArea;
      
      // Get the y-position for the peer average value
      const y = scales.y.getPixelForValue(peerAverage);
      
      // Draw the extended line across the full chart width
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(left, y);
      ctx.lineTo(right, y);
      ctx.lineWidth = peerAverageDataset.borderWidth || 2;
      ctx.strokeStyle = peerAverageDataset.borderColor || 'rgba(255, 128, 35, 0.6)';
      
      if (peerAverageDataset.borderDash) {
        ctx.setLineDash(peerAverageDataset.borderDash);
      }
      
      ctx.stroke();
      ctx.restore();
    }
  }
};

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, extendPeerAverageLinePlugin);


const AgentViewCharts = ({ agent }) => {
  const getSentimentColorClassAndLabel = (value) => {
    if (value > 0.5) return { colorClass: 'bg-green-500', label: 'VERY POSITIVE' };
    if (value > 0.2) return { colorClass: 'bg-green-300', label: 'POSITIVE' };
    if (value > -0.2) return { colorClass: 'bg-yellow-300', label: 'NEUTRAL' };
    if (value > -0.5) return { colorClass: 'bg-orange-500', label: 'NEGATIVE' };
    return { colorClass: 'bg-red-700', label: 'VERY NEGATIVE' };
  };

  const prepareChartData = (data, label, color, chartType = 'bar', isDynamic = false, timeUnit = 'hour', allDayGroups = [], peerAverage = null) => {
    console.log("Got filtered data:", data)
    if (!data) {
      return {}
    }
    
    let labels;
    let dataToUse;
    
    if (timeUnit === 'hour') {
      const startIndex = data.findIndex(val => val !== 0);
      const endIndex = data.length - [...data].reverse().findIndex(val => val !== 0);
      dataToUse = data.slice(startIndex, endIndex);
      
      labels = dataToUse.map((_, index) => {
        const hour = (startIndex + index) % 24;
        return moment().hour(hour).format('h A');
      });
    } else if (timeUnit === 'day') {
      dataToUse = data;
      labels = allDayGroups.map(dateStr => moment(dateStr).format('MMM D'));
    } else {
      dataToUse = data;
      labels = [];
    }

    const datasets = [{
      label,
      data: dataToUse,
      backgroundColor: isDynamic ? 
        (context) => {
          const { colorClass } = getSentimentColorClassAndLabel(context.raw);
          // Convert Tailwind classes to RGBA colors
          switch(colorClass) {
            case 'bg-green-500': return 'rgba(34, 197, 94, 0.8)';
            case 'bg-green-300': return 'rgba(134, 239, 172, 0.8)';
            case 'bg-yellow-300': return 'rgba(253, 224, 71, 0.8)';
            case 'bg-orange-500': return 'rgba(249, 115, 22, 0.8)';
            case 'bg-red-700': return 'rgba(185, 28, 28, 0.8)';
            default: return color;
          }
        } : color,
      borderColor: chartType === 'line' ? color : undefined,
      fill: chartType === 'line' ? false : undefined,
      pointRadius: chartType === 'line' ? 6 : undefined,
      pointHoverRadius: chartType === 'line' ? 8 : undefined,
      order: 2,
    }];

    if (peerAverage !== null) {
      datasets.push({
        type: 'line',
        label: 'Peer Average',
        data: new Array(labels.length).fill(peerAverage),
        borderColor: 'rgba(255, 128, 35, 0.6)',
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
        fill: true,
        order: 1,
        // Make the original line invisible, since our plugin will draw the extended line
        segment: {
          borderColor: 'transparent'
        }
      });
    }

    return {
      labels,
      datasets
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        font: {
          size: 16,
        },
      },
      tooltip: {
        enabled: true,
        mode: 'nearest',
        intersect: false,
        callbacks: {
          title: function(tooltipItems) {
            // Check if this is the peer average dataset
            if (tooltipItems.length > 0 && tooltipItems[0].datasetIndex === 1) {
              return 'Peer Average';
            }
            return tooltipItems[0].label;
          },
          label: function(context) {
            return  `Average: ${context.formattedValue}`;
          }
        }
      },
    },
    interaction: {
      mode: 'nearest',
      intersect: false,
    },
  };

  let callsData = [];
  let professionalismData = [];
  let sentimentData = [];
  let dateTimeRangeName = 'Day';
  
  if ('callsPerHour' in agent) {
    dateTimeRangeName = 'Hour';
    callsData = prepareChartData(agent.callsPerHour, 'Agent Calls per Hour', 'rgba(56, 126, 252, 0.6)', 'bar', false, 'hour');
    professionalismData = prepareChartData(
      agent.professionalismByHour, 
      'Agent Professionalism by Hour', 
      'rgba(20, 101, 250, 0.6)', 
      'line', 
      false, 
      'hour',
      [],
      agent.peerAverages?.averageProfessionalism
    );
    sentimentData = prepareChartData(
      agent.sentimentByHour, 
      'Agent Sentiment by Hour', 
      'rgba(0, 123, 255, 0.6)', 
      'bar', 
      true, 
      'hour',
      [],
      agent.peerAverages?.averageSentiment
    );
  } else if ('callsPerDay' in agent) {
    dateTimeRangeName = 'Day';
    callsData = prepareChartData(agent.callsPerDay, 'Agent Calls per Day', 'rgba(56, 126, 252, 0.6)', 'bar', false, 'day', agent.allDayGroups);
    professionalismData = prepareChartData(
      agent.professionalismByDay, 
      'Agent Professionalism by Day', 
      'rgba(20, 101, 250, 0.6)', 
      'line', 
      false, 
      'day', 
      agent.allDayGroups,
      agent.peerAverages?.averageProfessionalism
    );
    sentimentData = prepareChartData(
      agent.sentimentByDay, 
      'Agent Sentiment by Day', 
      'rgba(0, 123, 255, 0.6)', 
      'bar', 
      true, 
      'day', 
      agent.allDayGroups,
      agent.peerAverages?.averageSentiment
    );
  }
  
  // Base options for professionalism chart
  const professionalismOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        display: false,
      },
      title: {
        ...chartOptions.plugins.title,
        text: 'Agent Professionalism by ' + dateTimeRangeName,
        display: true
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      }
    }
  };

  // Base options for sentiment chart
  const sentimentOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      legend: {
        display: false,
      },
      title: {
        ...chartOptions.plugins.title,
        text: 'Agent Sentiment by ' + dateTimeRangeName,
        display: true
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      }
    }
  };

  const callsOptions = {
    ...chartOptions, 
    plugins: {
      ...chartOptions.plugins,
      legend: {
        display: false,
      },
      title: {
        ...chartOptions.plugins.title, 
        text: 'Agent Calls per ' + dateTimeRangeName
      }
    }
  };

  return (
    <div className="agent-view-charts">
      <div className="charts-container">
        <div className="chart-wrapper">
          <Bar data={callsData} options={callsOptions} />
        </div>
        <div className="chart-wrapper">
          <Line 
            data={professionalismData} 
            options={professionalismOptions} 
          />
        </div>
        <div className="chart-wrapper">
          <Bar
            data={sentimentData}
            options={sentimentOptions}
          />
        </div>
      </div>
    </div>
  );
};

export default AgentViewCharts;